import { useState } from 'react';
import type { ReactElement } from 'react';
import { AlertTriangle, CheckCircle2, CloudOff } from 'lucide-react';
import { assetToast } from '@xdocs/i18n';
import { Button, ConfirmDialog, EmptyState, Skeleton, cn, useI18n, useToast } from '@xdocs/ui';
import { DrivePickerModal } from './DrivePickerModal.js';
import { useDriveConnection } from './useDriveConnection.js';
import { useDriveT } from './useDriveT.js';
import styles from './DriveSettingsPage.module.css';

/**
 * `/settings/drive` (F6): connect/status/disconnect management + the
 * "Import from Drive" entry point into the picker (`DrivePickerModal`).
 * Imports here always land in the explorer ROOT (`targetFolderId: null`) —
 * this page has no folder context of its own; a folder-scoped import would
 * be launched from `/explorer` instead (out of this page's job).
 */
export function DriveSettingsPage(): ReactElement {
  const { locale } = useI18n();
  const { t } = useDriveT();
  const { push: pushToast } = useToast();
  const connection = useDriveConnection();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | undefined>(undefined);

  async function handleConnect(): Promise<void> {
    const authUrl = await connection.connect();
    window.location.assign(authUrl);
  }

  async function handleDisconnect(): Promise<void> {
    setDisconnectError(undefined);
    try {
      await connection.disconnect();
      setConfirmDisconnectOpen(false);
      pushToast(assetToast(locale, 'success', t('driveSettings:toast.disconnected.action'), t('driveSettings:title')));
    } catch (err) {
      const alert = err as { message?: string };
      setDisconnectError(alert.message ?? 'errors:code.INTERNAL');
    }
  }

  return (
    <div className={cn(styles['page'])}>
      <div className={cn(styles['header'])}>
        <h1 className={cn(styles['title'])}>{t('driveSettings:title')}</h1>
        <p className={cn(styles['description'])}>{t('driveSettings:description')}</p>
      </div>

      {import.meta.env.DEV && <p className={cn(styles['devNotice'])}>{t('driveSettings:devStub.notice')}</p>}

      {connection.isLoading && (
        <div aria-busy="true" className={cn(styles['card'])}>
          <Skeleton shape="text-line" width="60%" />
          <Skeleton shape="text-line" width="40%" />
        </div>
      )}

      {connection.isError && (
        <EmptyState kind="error" title={t('driveSettings:status.error.title')} action={{ label: t('driveSettings:action.retry'), onClick: connection.onRetry }} />
      )}

      {!connection.isLoading && !connection.isError && connection.status && (
        <div className={cn(styles['card'])}>
          {connection.status.connected ? (
            <>
              <div className={cn(styles['statusRow'])}>
                <CheckCircle2 size={20} className={cn(styles['statusIconOk'])} aria-hidden="true" />
                <div>
                  <p className={cn(styles['statusTitle'])}>{t('driveSettings:status.connected.title')}</p>
                  {connection.status.email && (
                    <p className={cn(styles['statusDescription'])}>{t('driveSettings:status.connected.description', { email: connection.status.email })}</p>
                  )}
                </div>
              </div>

              {connection.status.isExpired && (
                <p role="alert" className={cn(styles['expiredNote'])}>
                  <AlertTriangle size={14} aria-hidden="true" /> {t('driveSettings:status.connected.expired')}
                </p>
              )}

              <div className={cn(styles['actions'])}>
                {connection.status.isExpired ? (
                  <Button variant="primary" state={connection.isConnecting ? 'loading' : 'idle'} onClick={() => void handleConnect()}>
                    {t('driveSettings:action.reconnect')}
                  </Button>
                ) : (
                  <Button variant="secondary" onClick={() => setPickerOpen(true)}>
                    {t('driveSettings:action.import')}
                  </Button>
                )}
                <Button variant="ghost" onClick={() => setConfirmDisconnectOpen(true)}>
                  {t('driveSettings:action.disconnect')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className={cn(styles['statusRow'])}>
                <CloudOff size={20} className={cn(styles['statusIconOff'])} aria-hidden="true" />
                <div>
                  <p className={cn(styles['statusTitle'])}>{t('driveSettings:status.disconnected.title')}</p>
                  <p className={cn(styles['statusDescription'])}>{t('driveSettings:status.disconnected.description')}</p>
                </div>
              </div>
              <div className={cn(styles['actions'])}>
                <Button variant="primary" state={connection.isConnecting ? 'loading' : 'idle'} onClick={() => void handleConnect()}>
                  {t('driveSettings:action.connect')}
                </Button>
              </div>
              {connection.connectError && (
                <p role="alert" className={cn(styles['expiredNote'])}>
                  {connection.connectError}
                </p>
              )}
            </>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmDisconnectOpen}
        onOpenChange={setConfirmDisconnectOpen}
        action={t('driveSettings:disconnect.confirm')}
        assetName={t('driveSettings:title')}
        onConfirm={handleDisconnect}
        {...(disconnectError ? { error: disconnectError } : {})}
      />

      <DrivePickerModal open={pickerOpen} onClose={() => setPickerOpen(false)} targetFolderId={null} />
    </div>
  );
}
